import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';

const navItems = [
  { label: '首页', to: '/' },
  { label: '电影', to: '/category/movie' },
  { label: '电视剧', to: '/category/tv' },
  { label: '综艺', to: '/category/variety' },
  { label: '我的收藏', to: '/favorites' },
  { label: '关于我们', to: '/about' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const submitSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
  };

  const solid = scrolled || !isHome || menuOpen;

  return (
    <header
      className={`w-full transition-colors duration-300 ${
        solid
          ? 'bg-background-50/95 backdrop-blur border-b border-background-200'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 h-16 flex items-center gap-4 lg:gap-8">
        <Link to="/" className="flex items-center gap-2.5 shrink-0 group">
          <span className="w-9 h-9 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center text-lg">
            <i className="ri-video-fill"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span
              className={`font-heading font-black tracking-tight text-lg ${
                solid ? 'text-foreground-950' : 'text-white'
              }`}
            >
              丝瓜视频
            </span>
            <span
              className={`font-label text-[10px] tracking-[0.2em] uppercase mt-1 ${
                solid ? 'text-foreground-500' : 'text-white/70'
              }`}
            >
              Sigua TV
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1 flex-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                  isActive
                    ? solid
                      ? 'text-primary-600 bg-primary-50'
                      : 'text-white bg-white/15'
                    : solid
                      ? 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
                      : 'text-white/85 hover:text-white hover:bg-white/10'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <form onSubmit={submitSearch} className="hidden md:flex items-center ml-auto relative">
          <span
            className={`absolute left-3 w-4 h-4 flex items-center justify-center ${
              solid ? 'text-foreground-500' : 'text-white/70'
            }`}
          >
            <i className="ri-search-line text-base"></i>
          </span>
          <input
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder="搜索影片、导演、演员"
            aria-label="搜索影视内容"
            className={`w-48 lg:w-64 h-9 pl-9 pr-3 rounded-md text-sm outline-none transition-all focus:w-56 lg:focus:w-72 border ${
              solid
                ? 'bg-background-100 border-background-200 text-foreground-950 placeholder:text-foreground-500 focus:border-primary-400 focus:bg-background-50'
                : 'bg-white/10 border-white/25 text-white placeholder:text-white/60 focus:bg-white/20'
            }`}
          />
        </form>

        <button
          type="button"
          onClick={() => setMenuOpen((prev) => !prev)}
          aria-label="打开菜单"
          aria-expanded={menuOpen}
          className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-md ml-auto cursor-pointer ${
            solid ? 'text-foreground-950 hover:bg-background-100' : 'text-white hover:bg-white/10'
          }`}
        >
          <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
        </button>
      </div>

      {menuOpen && (
        <div className="lg:hidden border-t border-background-200 bg-background-50 px-4 py-4 animate-soft-fade">
          <form onSubmit={submitSearch} className="relative mb-3">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-500">
              <i className="ri-search-line text-base"></i>
            </span>
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索影片、导演、演员"
              aria-label="搜索影视内容"
              className="w-full h-10 pl-9 pr-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-primary-400"
            />
          </form>
          <nav className="grid grid-cols-2 gap-2">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `px-3 py-2.5 rounded-md text-sm font-medium ${
                    isActive
                      ? 'bg-primary-50 text-primary-600'
                      : 'bg-background-100 text-foreground-800'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}